import React, { useEffect, useMemo } from 'react';
import { track } from '@amplitude/analytics-browser';
import { Alert, Button, Link, Spinner } from '@nimbus-ds/components';
import { useBankAccount, useIdentityValidation, useToast } from 'commons/hooks';
import { KycDocumentErrorsType } from 'commons/types';
import { getLegalEntityType, useTranslationWithPrefix } from 'commons/utils';
import {
  dataDogLogger,
  DataDogLoggerActionTypes,
} from 'commons/utils/dataDogLogger';
import {
  getKycErrorsTranslation,
  getPendingDocNavigationFlags,
  hasKycErrorHelpLink,
  hasKycErrors,
  isNonRemediableKycError,
  remediatesWebsite,
  shouldNavigateForPendingDoc,
} from 'commons/utils/getKycError';
import {
  useIdValidationRoutes,
  useKycInfoMutation,
  // eslint-disable-next-line boundaries/element-types
} from 'domains/IdentityValidation/NewIdentityValidation/hooks';
import { PendingTrialPeriodAlertPage } from '../PendingTrialPeriodAlert';

interface InterfacePendingDocIdentityAlert {
  kycDocumentsErrors: KycDocumentErrorsType[];
  blockedTrialPeriod?: boolean;
  page: PendingTrialPeriodAlertPage;
}

function areKycErrorsEqual(
  a: KycDocumentErrorsType[],
  b: KycDocumentErrorsType[],
): boolean {
  if (a.length !== b.length) return false;
  const key = (error: KycDocumentErrorsType) =>
    `${error.documentName}:${error.errorCode}`;
  const counts = new Map<string, number>();
  b.forEach((error) => {
    const errorKey = key(error);
    counts.set(errorKey, (counts.get(errorKey) ?? 0) + 1);
  });

  return a.every((error) => {
    const errorKey = key(error);
    const count = counts.get(errorKey) ?? 0;
    if (count === 0) return false;
    counts.set(errorKey, count - 1);
    return true;
  });
}

const PendingDocIdentityAlert = ({
  kycDocumentsErrors,
  blockedTrialPeriod,
  page,
}: InterfacePendingDocIdentityAlert): JSX.Element | null => {
  const { t } = useTranslationWithPrefix(
    'configuration.personalData.kycV2.pending_documentation',
  );

  const identityValidation = useIdentityValidation();
  const bankAccount = useBankAccount();
  const { showToastError } = useToast();

  const { mutateAsync, isLoading: isMutating } = useKycInfoMutation({});

  const { goToNextRoute, getNextRoute } = useIdValidationRoutes();

  const resolvedTranslationKey = getKycErrorsTranslation(kycDocumentsErrors);

  // Only gates the owner count reported before dlocal re-analyses the store, so
  // it stays out of the navigation selector shared with useIdValidationRoutes.
  const { hasWebsiteError } = hasKycErrors(kycDocumentsErrors);

  const navigationFlags = getPendingDocNavigationFlags(kycDocumentsErrors);
  const {
    hasStatuteError,
    hasDocumentsMissingFieldsError,
    hasLegalRepresentativeNotFoundInCompanyError,
  } = navigationFlags;

  // An unmapped set still gets the generic copy; the effect below reports it.
  const translationPrefix = resolvedTranslationKey || 'generalError';
  const title = t(`${translationPrefix}.title`);
  const description = t(`${translationPrefix}.description`);

  // A rejection the merchant cannot act on gets no CTA: the only thing a button
  // could do here is restart a validation that ends in the same rejection.
  const hasNoRemediation = isNonRemediableKycError(resolvedTranslationKey);
  const remediationIsWebsite = remediatesWebsite(resolvedTranslationKey);
  const hasHelpLink = hasKycErrorHelpLink(resolvedTranslationKey);

  useEffect(() => {
    if (!resolvedTranslationKey) {
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent
      kycDocumentsErrors.forEach(({ errorCode, documentName }) => {
        dataDogLogger.generate({
          actionMessage: 'kyc_unmapped_error',
          actionOwner: 'PendingDocIdentityAlert',
          actionType: DataDogLoggerActionTypes.Error,
          actionData: { errorCode, documentName },
        });
      });
    }
  }, [resolvedTranslationKey, kycDocumentsErrors]);

  const pausedPrefix = blockedTrialPeriod ? `${t('pausedPrefix')} ` : '';

  const trackingProperties = useMemo(
    () => ({
      page,
      blocked_trial_period: blockedTrialPeriod,
      legal_entity_type: getLegalEntityType(bankAccount.data?.fiscalDocument),
    }),
    [page, blockedTrialPeriod, bankAccount.data?.fiscalDocument],
  );

  useEffect(() => {
    track('pago-nube-error-documentacion-view', trackingProperties);
  }, [trackingProperties]);

  const logNoNavigation = (reason: string) => {
    dataDogLogger.generate({
      actionMessage: 'kyc_cta_no_navigation',
      actionOwner: 'PendingDocIdentityAlert',
      actionType: DataDogLoggerActionTypes.Error,
      actionData: { reason, page, kycDocumentsErrors },
    });
  };

  const navigateOrLog = (reason: string) => {
    if (getNextRoute()) {
      goToNextRoute();
    } else {
      logNoNavigation(reason);
    }
  };

  const handleOnClick = async () => {
    track('pago-nube-error-documentacion-click', trackingProperties);

    // bankAccount is required to determine if the store is juridical or fisical, so goToNextRoute() can select the next route
    if (bankAccount.isError) {
      // Wait for the retry before deciding what the user sees: showing the
      // error toast beforehand would report a failure the retry may have
      // already fixed.
      const { isError: stillFailing } = await bankAccount.refetch();
      if (stillFailing) {
        showToastError(t('bankAccountUnavailable'));
        logNoNavigation('bank_account_error');
      }
      return;
    }

    if (hasWebsiteError && !hasStatuteError) {
      try {
        await mutateAsync(1); // numberOfOwners = 1
      } catch (error) {
        dataDogLogger.generate({
          actionMessage: 'kyc_cta_report_owners_failed',
          actionOwner: 'PendingDocIdentityAlert',
          actionType: DataDogLoggerActionTypes.Error,
          actionData: { page },
          error,
        });
      }
    }

    // The CTA has to serve the error the message names. A website message asks
    // the merchant to change their own store, so the click re-checks the
    // validation: navigating to a document upload they were never told about is
    // the mismatch this branch order exists to prevent. Whatever else is in the
    // set keeps its own message, and its own CTA, once this one clears.
    if (remediationIsWebsite) {
      track('pago-nube-prueba-de-vida-click-cargo-productos');
      const previousErrors = kycDocumentsErrors;
      const { data, isError } = await identityValidation.refetch();
      if (isError) {
        showToastError(t('websiteErrorStillPresent'));
        logNoNavigation('website_error_refetch_failed');
        return;
      }
      const errorsUnchanged = areKycErrorsEqual(
        data?.kycDocumentErrors ?? [],
        previousErrors,
      );
      if (errorsUnchanged) {
        showToastError(t('websiteErrorStillPresent'));
        logNoNavigation('website_error_unchanged_after_refetch');
      }
      return;
    }

    if (shouldNavigateForPendingDoc(navigationFlags)) {
      if (hasDocumentsMissingFieldsError) {
        track('pago-nube-prueba-de-vida-click-cargar-documentacion');
      } else if (hasLegalRepresentativeNotFoundInCompanyError) {
        track('pago-nube-prueba-de-vida-click-cargar-documentacion-adicional');
      }
      navigateOrLog('pending_doc_no_matching_route');
      return;
    }

    navigateOrLog('default_no_matching_route');
  };

  const isLoading =
    isMutating ||
    identityValidation.isRefetching ||
    bankAccount.isLoading ||
    bankAccount.isRefetching;

  return (
    <Alert
      show
      appearance="danger"
      title={title}
      data-testid="pending-doc-alert"
    >
      {pausedPrefix}
      {description}
      {hasHelpLink && (
        <Link
          as="a"
          href={t(`${translationPrefix}.linkUrl`)}
          target="_blank"
          rel="noopener noreferrer"
          appearance="primary"
        >
          {t(`${translationPrefix}.linkText`)}
        </Link>
      )}
      {!hasNoRemediation && (
        <Button
          appearance="neutral"
          onClick={handleOnClick}
          disabled={isLoading}
        >
          {isLoading && <Spinner size="small" />}
          {t(`${translationPrefix}.validateButton`)}
        </Button>
      )}
    </Alert>
  );
};

export default PendingDocIdentityAlert;
